import React, { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import Box from '../Box';
import type { BoxProps } from '../Box/Box';
import Text from '../Text';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & BoxProps;

const Input = forwardRef(
  (props: InputProps, ref: React.Ref<HTMLInputElement>) => {
    return (
      <Box
        as="input"
        ref={ref}
        width={1}
        height={32}
        color="input.text"
        py={2}
        px={3}
        fontSize={2}
        borderWidth={1}
        borderColor={props.borderColor ?? 'input.border'}
        borderStyle="solid"
        bg={props.disabled ? 'input.disabledBackground' : 'input.background'}
        {...props}
      />
    );
  },
);

export default Input;

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  BoxProps;

export const TextArea = forwardRef(
  (props: TextAreaProps, ref: React.Ref<HTMLTextAreaElement>) => {
    return (
      <Box
        as="textarea"
        ref={ref}
        width={1}
        color="input.text"
        py={2}
        px={3}
        fontSize={2}
        borderWidth={1}
        borderColor="input.border"
        borderStyle="solid"
        bg={props.disabled ? 'input.disabledBackground' : 'input.background'}
        {...props}
      />
    );
  },
);

/**
 * Checkbox which immediately toggles the displayed value on click which makes the UX feel better
 * when there's no UI delay until the timeline has been recomputed before the new value can be shown
 */
export const OptimisticCheckbox = ({
  checked,
  onChange,
  label,
  labelColor,
  isSingleLine,
  disabled,
  ...restProps
}: {
  checked: boolean;
  label: string;
  labelColor?: string;
  isSingleLine?: boolean;
  disabled?: boolean;
  onChange: (newValue: boolean) => void;
} & Omit<BoxProps, 'color'>): JSX.Element => {
  const [shownValue, setShownValue] = useState<boolean>(checked);

  useEffect(() => {
    setShownValue(checked);
  }, [checked]);

  return (
    <Box as="label" {...restProps}>
      <Input
        type="checkbox"
        checked={shownValue}
        onChange={() => {
          setShownValue(!shownValue);
          setTimeout(() => {
            onChange(!shownValue);
          });
        }}
        size={16}
        verticalAlign="middle"
        disabled={disabled ?? false}
      />
      <Text
        color={labelColor}
        fontSize="2"
        marginLeft="1"
        isSingleLine={isSingleLine}
      >
        {label}
      </Text>
    </Box>
  );
};

export const InputWithoutArrows = styled(Input)`
  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;
